import {
  Alert,
  AlertDescription,
  Button,
  FormRoot,
  FormSelect,
  RHF,
  zodResolver,
} from "@devhop/ui";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { isAfter, isBefore } from "date-fns";
import { ArrowLeftIcon } from "lucide-react";
import { useEffect, useMemo, useRef } from "react";
import { z } from "zod";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { studentsApi } from "@/modules/students/presentation/api/client";
import { config } from "@/shared/lib/config";
import { formatDate } from "@/shared/lib/date-time";
import { formatSlotHours } from "@/shared/lib/private-lesson-slot-duration";
import { fetchLookupForInfinite, hydrateLookupItem } from "@/shared/lib/utils";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { FormInfiniteCombobox } from "@/shared/ui/FormInfiniteCombobox";
import { activePrivateLessonPackageOptionSuffix } from "@/modules/private-lessons/domain/lib/active-private-lesson-package-display";
import type { BulkScheduleRequestDTO } from "../../domain/contracts/bulk-schedule";
import {
  useBulkSchedulePrivateLessons,
  usePrivateLessonPackageQuery,
} from "../api/queries";
import { BulkScheduleForm } from "../ui/BulkScheduleForm";

type StudentLookupItem = { id: string; name: string; memberId: string };

const TopSchema = z.object({
  studentId: z.string().min(1, "ຕ້ອງເລືອກນັກຮຽນ"),
  packageId: z.string().min(1, "ຕ້ອງເລືອກແພັກເກດ"),
});

type TopValues = z.infer<typeof TopSchema>;

export function PrivateLessonBookingBulkCreatePage() {
  const nav = useNavigate({
    from: "/app/private-lessons/bookings/bulk-create",
  });
  const search = useSearch({
    from: "/app/private-lessons/bookings/bulk-create",
  });
  const bulkSchedule = useBulkSchedulePrivateLessons();
  const prevStudentIdRef = useRef<string | null>(null);

  const topMethods = RHF.useForm<TopValues>({
    resolver: zodResolver(TopSchema),
    defaultValues: {
      studentId: search.studentId ?? "",
      packageId: search.packageId ?? "",
    },
  });

  const { setValue } = topMethods;
  const studentId = topMethods.watch("studentId");
  const packageId = topMethods.watch("packageId");

  useEffect(() => {
    if (search.studentId) {
      setValue("studentId", search.studentId);
    }
    if (search.packageId) {
      setValue("packageId", search.packageId);
    }
  }, [search.studentId, search.packageId, setValue]);

  useEffect(() => {
    if (
      prevStudentIdRef.current !== null &&
      prevStudentIdRef.current !== studentId
    ) {
      setValue("packageId", "");
    }
    prevStudentIdRef.current = studentId || null;
  }, [studentId, setValue]);

  const { data: packagesData } = useQuery({
    queryKey: ["student", studentId, "active-private-lesson-packages", "bulk"],
    queryFn: async () => {
      if (!studentId)
        return { data: [], meta: { total: 0, offset: 0, limit: 100 } };
      return studentsApi.listPrivateLessonPackages(studentId, {
        limit: 100,
        offset: 0,
        filters: [{ field: "status", op: "eq", value: "active" }],
      });
    },
    enabled: !!studentId,
  });

  const activePackages = useMemo(() => {
    const packages = packagesData?.data ?? [];
    const today = new Date().toISOString().split("T")[0] ?? "";
    return packages.filter(
      (pkg) =>
        pkg.status === "active" &&
        isAfter(pkg.expireDate, today) &&
        pkg.remainingHours > 0,
    );
  }, [packagesData]);

  const packageSelectOptions = useMemo(
    () =>
      activePackages.map((pkg) => ({
        value: pkg.id,
        label: `${pkg.templateName}${activePrivateLessonPackageOptionSuffix(pkg)} (ເຫຼືອ ${formatSlotHours(pkg.remainingHours)} ຊົ່ວໂມງ, ໝົດ ${formatDate(pkg.expireDate)})`,
      })),
    [activePackages],
  );

  const { data: packageData, isLoading: packageLoading } =
    usePrivateLessonPackageQuery(packageId);

  const canSchedule =
    packageData &&
    packageData.status === "active" &&
    packageData.remainingHours > 0 &&
    !isBefore(packageData.expireDate, new Date());

  const handleBulkSubmit = async (values: BulkScheduleRequestDTO) => {
    try {
      await bulkSchedule.mutateAsync(values);
      nav({ to: "/app/private-lessons/bookings" });
    } catch {
      // Mutation surfaces toast
    }
  };

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "ການຈອງຮຽນສ່ວນຕົວ", to: "/app/private-lessons/bookings" },
            { label: "ຈອງຫຼາຍຄັ້ງ" },
          ]}
        />
      </Header>

      <Main>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h2 className="font-bold text-2xl tracking-tight">
              ຈອງຮຽນສ່ວນຕົວຫຼາຍຄັ້ງ
            </h2>
            <p className="text-muted-foreground">
              ເລືອກນັກຮຽນ ແລະແພັກເກດ — ຊ່ອງເວລາຕໍ່ຄັ້ງ: ເວລາເລີ່ມ + ຄວາມຍາວທີລະ 15 ນາທີ ຮອດ
              2 ຊົ່ວໂມງ; ສິ້ນເວລາຄິດໃຫ້ອັດຕະໂນມັດ
            </p>
          </div>
          <Button
            variant="outline"
            onClick={() => nav({ to: "/app/private-lessons/bookings" })}
          >
            <ArrowLeftIcon className="size-4" />
            ກັບຄືນ
          </Button>
        </div>

        <div className="mt-6 rounded-xl border bg-card p-6">
          <FormRoot
            methods={topMethods}
            onSubmit={topMethods.handleSubmit(() => {})}
          >
            <h3 className="mb-4 font-semibold text-lg">ນັກຮຽນ ແລະແພັກເກດ</h3>
            <div className="grid gap-4 md:grid-cols-2">
              <FormInfiniteCombobox<StudentLookupItem>
                name="studentId"
                label="ນັກຮຽນ"
                requiredMark
                queryKey={["students", "lookup"]}
                queryFn={(args) =>
                  fetchLookupForInfinite<StudentLookupItem>(
                    `${config.apiUrl}/students/lookup`,
                    args,
                  )
                }
                preloadQueryFn={(id) =>
                  hydrateLookupItem<StudentLookupItem>(
                    `${config.apiUrl}/students/lookup`,
                    id,
                  )
                }
                getLabel={(item) => `${item.name} (${item.memberId})`}
                getValue={(item) => item.id}
                placeholder="ເລືອກນັກຮຽນ..."
              />
              {studentId && activePackages.length > 0 ? (
                <FormSelect
                  name="packageId"
                  label="ແພັກເກດ"
                  requiredMark
                  options={packageSelectOptions}
                />
              ) : null}
            </div>
            {studentId && activePackages.length === 0 && (
              <Alert className="mt-4">
                <AlertDescription>
                  ນັກຮຽນບໍ່ມີແພັກເກດທີ່ໃຊ້ງານໄດ້ ຫຼື ໝົດຊົ່ວໂມງແລ້ວ
                </AlertDescription>
              </Alert>
            )}
          </FormRoot>
        </div>

        {packageId && (
          <div className="mt-6">
            {packageLoading ? (
              <p className="text-muted-foreground">ກຳລັງໂຫຼດແພັກເກດ...</p>
            ) : !packageData ? (
              <Alert>
                <AlertDescription>ບໍ່ພົບແພັກເກດ</AlertDescription>
              </Alert>
            ) : !canSchedule ? (
              <Alert>
                <AlertDescription>
                  ແພັກເກດນີ້ບໍ່ສາມາດກຳນົດຕາຕະລາງໄດ້ (ສະຖານະ, ໝົດອາຍຸ ຫຼື ບໍ່ມີຊົ່ວໂມງເຫຼືອ)
                </AlertDescription>
              </Alert>
            ) : (
              <>
                <div className="mb-4 rounded-lg border bg-muted/50 p-4 text-sm">
                  <p>
                    <span className="text-muted-foreground">ນັກຮຽນ:</span>{" "}
                    <span className="font-semibold">
                      {packageData.studentName} ({packageData.studentMemberId})
                    </span>
                  </p>
                  <p className="mt-1">
                    <span className="text-muted-foreground">ແພັກເກດ:</span>{" "}
                    <span className="font-semibold">
                      {packageData.templateName}
                    </span>
                    {" — "}
                    <span className="text-muted-foreground">ເຫຼືອ</span>{" "}
                    <span className="font-semibold">
                      {packageData.remainingHours} ຊົ່ວໂມງ
                    </span>
                  </p>
                </div>
                <BulkScheduleForm
                  key={packageId}
                  packageId={packageId}
                  remainingHours={packageData.remainingHours}
                  packageLocationKind={packageData.templateDefaultLocationKind}
                  onSubmit={handleBulkSubmit}
                  submitting={bulkSchedule.isPending}
                />
              </>
            )}
          </div>
        )}
      </Main>
    </>
  );
}
